import { css, useTheme } from '@fable/theme'
import { FlexBox, RoundImage, Text } from '@fable/components'
import { useTypedSelector } from '../../../../hooks'
import { getDatePattern, getDateString } from '../../../../utils'
import { useFeaturedEvent } from '../../context'
import { FeaturedEventRSVPButton } from './FeaturedEventRSVPButton'

export const FeaturedEventMasthead: React.VFC = () => {
  const { event } = useFeaturedEvent()
  const { colors, mediaQueries } = useTheme()
  const { isAuthenticated } = useTypedSelector(({ auth }) => auth)
  const hasIllustration = !!event.illustration?.url
  const datePattern = getDatePattern(event.date, 'MMMM d, yyyy @ haaa z')
  const date = getDateString({
    date: event.date,
    pattern: datePattern,
  })
  const isLight = event.theme === 'light'

  return (
    <div
      className={css`
        padding: 24px;
        padding-bottom: 36px;
        background-color: ${event.primaryColor};
        ${mediaQueries.mobileL} {
          padding: 36px;
        }
      `}
    >
      <div
        className={css`
          margin-bottom: 52px;
          width: 100%;
          height: min(64.167vw, 385px);
          position: relative;
          ${mediaQueries.mobileL} {
            margin-bottom: ${hasIllustration || !!event.book ? 76 : 40}px;
          }
        `}
      >
        <div
          className={css`
            position: relative;
            width: 100%;
            height: 100%;
            border-radius: 20px;
            border-top-right-radius: 90px;
            overflow: hidden;
          `}
        >
          <img
            data-testid="featuredEventImage"
            className={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: left;
              ${mediaQueries.mobileL} {
                object-position: center;
                position: absolute;
                top: 34px;
                left: 50%;
                transform: translateX(-50%) scale(1.2);
              }
            `}
            src={`${event.image?.url}?q=100` || ''}
            alt={`${event.title} Event`}
          />
        </div>
        {!event.book ? null : (
          <img
            data-testid="featuredEventBookCover"
            className={css`
              width: min(26.087vw, 108px);
              height: min(39.13vw, 162px);
              border-radius: 8px;
              object-fit: cover;
              position: absolute;
              bottom: -28px;
              left: 24px;
              ${mediaQueries.mobileL} {
                left: 14px;
              }
            `}
            src={event.book.bookCover}
            alt={`${event.book.title} Cover`}
          />
        )}
        {!hasIllustration ? null : (
          <img
            data-testid="featuredEventIllustration"
            className={css`
              width: min(62.319vw, 258px);
              position: absolute;
              bottom: 0;
              right: -52px;
              transform: translateY(min(39.13vw, 162px));
              ${mediaQueries.mobileL} {
                bottom: -150px;
                right: -19px;
                transform: translateY(0);
              }
            `}
            src={event.illustration?.url}
            alt={`${event.title} illustration`}
          />
        )}
      </div>
      <div
        className={css`
          position: relative;
        `}
      >
        {!!date ? (
          <Text
            type="subhead"
            sizing={{ base: 'S', mobileL: 'M' }}
            color={isLight ? colors.graphite : colors.almostWhite}
            text={date}
          />
        ) : null}
        <Text
          className={css`
            margin: 24px 0;
            max-width: ${hasIllustration ? 360 : 480}px;
          `}
          type="header"
          sizing={{ base: 'M', mobileL: 'L' }}
          color={isLight ? colors.graphite : colors.whiteFang}
          text={event.title}
        />
        <Text
          className={css`
            display: block;
            max-width: 390px;
          `}
          type="body"
          sizing="L"
          color={isLight ? colors.graphite : colors.whiteFang}
          text={event.excerpt}
        />
        <FlexBox
          className={css`
            margin-top: 21px;
          `}
          alignItems="center"
        >
          <RoundImage
            className={css`
              margin-right: 19px;
              border: 2px solid ${colors.saffron};
              flex-shrink: 0;
            `}
            src={event.host.avatar.url || ''}
            alt={`${event.host.name} Avatar`}
            size={439}
            diameter={'60px'}
          />
          <FlexBox flexDirection="column">
            <Text
              className={css`
                text-transform: uppercase;
                font-weight: 600;
              `}
              type="body"
              sizing="XS"
              text="Event Host:"
              color={isLight ? colors.graphite : colors.whiteFang}
            />
            <Text
              className={css`
                word-break: break-all;
              `}
              type="subhead"
              sizing={{ base: 'S', mobileL: 'M' }}
              text={event.host.handle || event.host.name}
              color={isLight ? colors.graphite : colors.whiteFang}
            />
          </FlexBox>
        </FlexBox>
        {!isAuthenticated ? null : <FeaturedEventRSVPButton />}
      </div>
    </div>
  )
}
